import { Box, CircularProgress, Typography } from '@mui/material';
import { getProgressColor } from './ProgressBar';

interface SiteProgressAvatarProps {
  slug?: string;
  name: string;
  progressPercentage: number;
  size?: number;
}

const DEFAULT_SIZE = 48;
const DEFAULT_THICKNESS = 3.6;

function getInitials(slug: string | undefined, name: string): string {
  if (slug && slug.length >= 2) return slug.slice(0, 2).toUpperCase();
  if (slug && slug.length === 1) return slug.toUpperCase();
  return name.charAt(0).toUpperCase();
}

export function SiteProgressAvatar({
  slug,
  name,
  progressPercentage,
  size = DEFAULT_SIZE,
}: SiteProgressAvatarProps) {
  const initials = getInitials(slug, name);
  const color = getProgressColor(progressPercentage);
  const thickness = DEFAULT_THICKNESS * (size / DEFAULT_SIZE);
  const fontSize = 0.8 * (size / DEFAULT_SIZE);

  return (
    <Box
      sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
    >
      {/* Grey track */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={thickness}
        sx={{
          position: 'absolute',
          color: 'grey.300',
          '.MuiCircularProgress-circle': { strokeLinecap: 'round' },
        }}
      />
      {/* Progress ring */}
      <CircularProgress
        variant="determinate"
        value={Math.min(progressPercentage, 100)}
        size={size}
        thickness={thickness}
        sx={{
          position: 'absolute',
          color,
          '.MuiCircularProgress-circle': { strokeLinecap: 'round' },
        }}
      />
      {/* Initials */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, fontSize: `${fontSize}rem`, lineHeight: 1 }}
        >
          {initials}
        </Typography>
      </Box>
    </Box>
  );
}
